import React, {
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import clsx from "clsx";

import MenuItems from "../MenuItems/MenuItems";
import Text from "../../common/Text/Text";
import { MenuContext } from "../Menu/MenuContext";
import { sendEvent, TrackingContext } from "../../../lib/util/AnalyticsUtils";
import { TextElement } from "../../../lib/util/LocaleUtils";
import Tooltip, {
	TooltipHandle,
	TooltipProps,
	TooltipTrigger,
} from "../../common/Tooltip/Tooltip";

export interface MenuItem {
	id: string;
	pathName?: string;
	includeState?: boolean;
	displayName?: TextElement;
	noRedirect?: boolean;
	disabled?: boolean;
}

export interface MenuDropdownProps extends Omit<TooltipProps, "content"> {
	items: MenuItem[];
	label?: React.ReactNode;
	selected?: string;
	allowSearch?: boolean;
	onSelect?: (item: MenuItem, groupId?: string) => void;
	invert?: boolean;
	readOnly?: boolean;
	usePortal?: boolean;
	preventOverflow?: boolean;
	onFilterItem?: (item: MenuItem) => boolean;
}

const MenuDropdown = (props: MenuDropdownProps) => {
	const trackingContext = useContext(TrackingContext);

	const [filteredItems, setFilteredItems] = useState<null | MenuItem[]>();

	const {
		items,
		overlay,
		label,
		selected,
		allowSearch,
		onVisibleChange,
		onSelect,
		invert,
		disabled,
		usePortal,
		readOnly,
		placement,
		tooltipClassName,
		children,
		className,
		preventOverflow,
		onFilterItem,
		...otherProps
	} = props;

	const trackToggle = useCallback(
		(open) => {
			const evt: any = {
				eventCategory: "Menu Dropdown",
				eventAction: open ? "open" : "close",
				eventLabel:
					typeof trackingContext === "string"
						? trackingContext
						: trackingContext.ga,
			};

			if (open) {
				evt.mixpanelProperties = {
					name: "Menu Dropdown Open",
					Action: "Open",
					Context:
						typeof trackingContext === "string" ? trackingContext : undefined,
				};

				if (typeof trackingContext !== "string" && trackingContext.mixpanel) {
					evt.mixpanelProperties = {
						...evt.mixpanelProperties,
						...trackingContext.mixpanel,
					};
				}
			}

			sendEvent(evt);
		},
		[trackingContext],
	);

	const handleOpenChange = useCallback(
		async (open: boolean) => {
			if (onVisibleChange) {
				onVisibleChange(open);
			}

			trackToggle(open);
		},
		[onVisibleChange, trackToggle],
	);

	const handleSelect = useCallback(
		(item: MenuItem) => {
			if (onSelect) {
				onSelect(item);
			}

			handleOpenChange(false);

			const evt: any = {
				eventCategory: "Menu Dropdown",
				eventAction: "select",
				eventLabel:
					typeof trackingContext === "string"
						? `${trackingContext} - ${item.id}`
						: `${trackingContext.ga} - ${item.id}`,
				mixpanelProperties: {
					name: "Menu Dropdown",
					Action: "Select",
					Item: item.id,
					Context:
						typeof trackingContext === "string" ? trackingContext : undefined,
				},
			};

			if (typeof trackingContext !== "string" && trackingContext.mixpanel) {
				evt.mixpanelProperties = {
					...evt.mixpanelProperties,
					...trackingContext.mixpanel,
				};
			}

			sendEvent(evt);
		},
		[handleOpenChange, onSelect, trackingContext],
	);

	const tooltipRef = useRef<TooltipHandle | null>(null);

	const itemsContent = useMemo(
		() => (
			<>
				{overlay && <hr />}
				{label && <Text tag={"label"}>{label}</Text>}
				<MenuItems
					onFilterItem={onFilterItem}
					autoFocus={items.length <= 15}
					selected={selected}
					onSelect={handleSelect}
					filteredRoutes={filteredItems || items}
					routes={items}
					allowSearch={allowSearch}
					onFilter={setFilteredItems}
				/>
			</>
		),
		[
			onFilterItem,
			allowSearch,
			filteredItems,
			handleSelect,
			items,
			label,
			overlay,
			selected,
		],
	);

	const classes = clsx(
		invert && "--invert",
		disabled && "--disabled",
		readOnly && "--readOnly",
	);

	const triggers = useMemo<TooltipTrigger[]>(() => ["click"], []);

	return (
		<MenuContext.Provider value={tooltipRef.current!}>
			<Tooltip
				{...otherProps}
				ref={tooltipRef}
				overlay={overlay}
				triggers={triggers}
				disabled={readOnly || disabled}
				overflow={!(preventOverflow === undefined ? false : preventOverflow)}
				onVisibleChange={handleOpenChange}
				content={itemsContent}
				placement={placement || "bottom"}
				className={clsx("MenuDropdown", classes, className)}
				tooltipClassName={clsx("MenuDropdown__menu", classes, tooltipClassName)}
			>
				{children}
			</Tooltip>
		</MenuContext.Provider>
	);
};

export default React.memo(MenuDropdown);
